import {
    AnalyticsUserEvent,
    type CmmArtifactFinalPageExperiment_ExperimentParam_Value,
} from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSetAtom } from 'jotai';
import { type ReactNode, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledInput,
    isNonMobileWeb,
    Shadows,
    Spinner,
    View,
    YStack,
} from 'smoothie';
import z from 'zod';

import {
    GENERAL_DISCLOSURES_TEXT,
    NewMemberAccountDisclosure,
} from '../../../banking/components/Disclosures';
import { isEmail } from '../../../common/misc';
import {
    captureMessage,
    track,
    trackGrowthMarketingEvent,
} from '../../../logger/Logger';
import { useUpsertVisitor } from '../../../user/hooks/useUpsertVisitor';
import { STYTCH_ERROR_MESSAGE_BY_TYPE } from '../../constants';
import { useCreateStytchAccount } from '../../hooks/start-membership/useCreateStytchAccount';
import { useReturningMemberPasswordReset } from '../../hooks/start-membership/useReturningMemberPasswordReset';
import { useStytchSession, useStytchUser } from '../../hooks/useAuth';
import { isUserSetupInProgressAtom } from '../../state/AuthAtoms';
import { PasswordStrengthChecker } from '../PasswordStrengthChecker';
import ShowPasswordToggle from '../ShowPasswordToggle';
import {
    ActiveAccountSignInMessage,
    ReturningMemberCheckEmailMessage,
    ReturningMemberErrorMessage,
} from './ReturningMemberMessages';

const createAccountSchema = z.object({
    email: z.string().refine(isEmail, 'Please enter a valid email address.'),
    password: z.string().min(1, 'Please enter a password.'),
});
type CreateAccountSchema = z.infer<typeof createAccountSchema>;

const AccountFormCard = ({ children }: { children: ReactNode }) => (
    <YStack gap={24}>
        <YStack
            padding={24}
            borderWidth={1}
            borderColor="$white"
            borderRadius={24}
            backgroundColor="$white"
            {...Shadows.SMALL}
        >
            {children}
        </YStack>
        <BodySmallRegular color="$gray1000">
            {GENERAL_DISCLOSURES_TEXT}
        </BodySmallRegular>
    </YStack>
);

/**
 * Reusable account-creation form (email + password + disclosures + submit).
 *
 * This component owns the Stytch account-creation flow but delegates
 * post-success behaviour (navigation, atom updates) to the caller via
 * `onSuccess`.
 */
export const ConvMmAccountForm = ({
    onSuccess,
    artifactFinalPageSegment,
}: {
    /** Called after the Stytch account is successfully created (or session already exists). */
    onSuccess: (email: string) => void;
    artifactFinalPageSegment: CmmArtifactFinalPageExperiment_ExperimentParam_Value;
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [authError, setAuthError] = useState<Error | null>(null);
    const [isPasswordValid, setIsPasswordValid] = useState(true);
    const {
        hasTriggeredReturningMemberPasswordReset,
        hasMatchedActiveAccount,
        hasEncounteredError,
        handleDuplicateEmail,
    } = useReturningMemberPasswordReset();
    const { user: stytchUser } = useStytchUser();
    const { session: stytchSession } = useStytchSession();
    const upsertVisitor = useUpsertVisitor();
    const setIsUserSetupInProgress = useSetAtom(isUserSetupInProgressAtom);

    const form = useForm<CreateAccountSchema>({
        resolver: zodResolver(createAccountSchema),
        defaultValues: { email: '', password: '' },
    });
    const watchedEmail = form.watch('email');
    const watchedPassword = form.watch('password');

    const { createStytchAccount, isCreatingStytchAccount } =
        useCreateStytchAccount();

    const onPasswordStrengthChange = useCallback(() => {
        setAuthError(null);
    }, []);

    const onCreateStytchAccountSuccess = useCallback(
        (email: string) => {
            upsertVisitor();
            track(
                AnalyticsUserEvent.SIGNUP_FUNNEL_NEW_MEMBER_ACCOUNT_CREATED,
                { artifactFinalPageSegment },
                true,
            );
            trackGrowthMarketingEvent(
                AnalyticsUserEvent.SIGNUP_FUNNEL_SURVEY_EMAIL_SUBMIT,
                { email },
            );
            setIsUserSetupInProgress(true);
            onSuccess(email);
        },
        [onSuccess, artifactFinalPageSegment, upsertVisitor],
    );

    const onCreateStytchAccountError = useCallback(
        async (error: Error) => {
            // Returning/active member — trigger a reset email instead of
            // showing "email already in use". Backend decides which case it is.
            if (
                error.message === STYTCH_ERROR_MESSAGE_BY_TYPE.duplicate_email
            ) {
                await handleDuplicateEmail(form.getValues('email'));
                return;
            }

            form.setError('root', {
                type: 'manual',
                message: error.message,
            });
            setAuthError(error);
        },
        [form, handleDuplicateEmail],
    );

    const onFormSubmit = useCallback(
        (formData: CreateAccountSchema) => {
            setAuthError(null);

            if (stytchUser && stytchSession) {
                captureMessage(
                    'Authentication account creation skipped -- session already exists',
                );
                onCreateStytchAccountSuccess(formData.email);
                return;
            }

            void createStytchAccount({
                email: formData.email,
                password: formData.password,
                onSuccess: () => {
                    onCreateStytchAccountSuccess(formData.email);
                },
                onError: onCreateStytchAccountError,
            });
        },
        [
            createStytchAccount,
            onCreateStytchAccountSuccess,
            onCreateStytchAccountError,
            stytchUser,
            stytchSession,
        ],
    );

    const hasAuthError =
        authError?.message != null && authError.message.length > 0;

    const isSubmitDisabled =
        isCreatingStytchAccount ||
        !isEmail(watchedEmail) ||
        !isPasswordValid ||
        hasAuthError;

    if (hasEncounteredError) {
        return (
            <AccountFormCard>
                <ReturningMemberErrorMessage />
            </AccountFormCard>
        );
    }

    if (hasMatchedActiveAccount) {
        return (
            <AccountFormCard>
                <ActiveAccountSignInMessage />
            </AccountFormCard>
        );
    }

    if (hasTriggeredReturningMemberPasswordReset) {
        return (
            <AccountFormCard>
                <ReturningMemberCheckEmailMessage />
            </AccountFormCard>
        );
    }

    return (
        <AccountFormCard>
            <ControlledInput
                name="email"
                label="Email address"
                placeholder="Enter email address"
                autoComplete="email"
                autoCapitalize="none"
                inputMode="email"
                keyboardType="email-address"
                textContentType="username"
                control={form.control}
                register={form.register}
                errors={form.formState.errors}
                onSubmitEditing={() => {
                    form.setFocus('password');
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={isNonMobileWeb}
                disabled={isCreatingStytchAccount}
            />

            <View marginTop={16}>
                <ControlledInput
                    name="password"
                    label="Password"
                    placeholder="Create a password"
                    autoCapitalize="none"
                    autoComplete="new-password"
                    inputMode="text"
                    keyboardType="default"
                    textContentType="password"
                    secureTextEntry={!showPassword}
                    control={form.control}
                    register={form.register}
                    errors={form.formState.errors}
                    onSubmitEditing={form.handleSubmit(onFormSubmit)}
                    rightSideContent={
                        <ShowPasswordToggle
                            showPassword={showPassword}
                            setShowPassword={setShowPassword}
                        />
                    }
                    disabled={isCreatingStytchAccount}
                />
                <PasswordStrengthChecker
                    password={watchedPassword}
                    onValidationChange={setIsPasswordValid}
                    onStrengthCheckChange={onPasswordStrengthChange}
                />
            </View>

            <View
                animation="quick"
                opacity={hasAuthError ? 1 : 0}
                height={hasAuthError ? 48 : 0}
                marginTop={hasAuthError ? 16 : 0}
                overflow="hidden"
                animateOnly={['opacity', 'height', 'marginTop']}
            >
                <BodySmallRegular color="$red1100">
                    {authError?.message ?? ''}
                </BodySmallRegular>
            </View>

            <View marginTop={16}>
                <NewMemberAccountDisclosure />
            </View>

            <View marginTop={16}>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={form.handleSubmit(onFormSubmit)}
                    disabled={isSubmitDisabled}
                    text={isCreatingStytchAccount ? '' : 'Create account'}
                    icon={isCreatingStytchAccount ? <Spinner /> : null}
                />
            </View>
        </AccountFormCard>
    );
};
